// frontend/src/services/games/xpService.ts
//
// Paylaşılan XP servisi. Şu an için localStorage'da tutulur.
//
// NOT (backend senkronizasyonu hazırlığı): Bu dosyanın dışa açtığı fonksiyon imzaları
// (getTotalXp, awardXp) sabit tutulmuştur. İleride bir backend eklendiğinde, bu
// fonksiyonların içi bir API çağrısına yönlendirilebilir (örn. optimistic local
// update + arka planda POST /api/games/xp senkronizasyonu) — çağıran kodun (oyun sayfaları)
// değişmesi gerekmez.

import { authService } from '../../services/auth.service';
import { useAuthStore } from '../../store/authStore';

const API_BASE = (import.meta.env.PROD && (import.meta.env.VITE_API_BASE_URL === 'http://localhost:8080' || !import.meta.env.VITE_API_BASE_URL))
  ? ''
  : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');

export interface XpState {
  total: number;
  perGame: Record<string, number>;
}

const STORAGE_KEY = 'minikcoder:xp:v1';

function emptyState(): XpState {
  return { total: 0, perGame: {} };
}

function read(): XpState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : emptyState();
  } catch {
    return emptyState();
  }
}

function write(state: XpState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // yoksay
  }
}

export interface AwardXpInput {
  gameId: string;
  amount: number;
  reason?: string;
}

export interface AwardXpResult {
  amount: number;
  total: number;
}

export function getTotalXp(): number {
  return read().total;
}

export function getXpForGame(gameId: string): number {
  return read().perGame[gameId] ?? 0;
}

async function syncXpToBackend(input: AwardXpInput): Promise<void> {
  const token = await authService.getAccessToken();
  if (!token) return;

  try {
    const res = await fetch(`${API_BASE}/api/games/xp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => null);
      console.warn('[games/xp] backend sync failed:', res.status, body);
      return;
    }

    // Backend güncellemesi başarılıysa profil verisini yenileyelim.
    const fetchProfile = useAuthStore.getState().fetchProfile;
    if (fetchProfile) {
      await fetchProfile();
    }
  } catch (err) {
    console.warn('[games/xp] backend sync error:', err);
  }
}

export function awardXp(input: AwardXpInput): AwardXpResult {
  const amount = Math.max(0, Math.round(input.amount));
  if (amount === 0) return { amount: 0, total: read().total };

  const state = read();
  const nextTotal = state.total + amount;
  const nextPerGame = {
    ...state.perGame,
    [input.gameId]: (state.perGame[input.gameId] ?? 0) + amount,
  };
  write({ total: nextTotal, perGame: nextPerGame });

  void syncXpToBackend(input);

  return { amount, total: nextTotal };
}
